<script setup lang="ts">
import { computed, ref } from 'vue';

import { useFloatingPosition } from '@/shared/lib/composables';
import { OverlayTeleport } from '@/shared/ui/overlay';

import type { TimeInputEmits } from './useTimeInput';

import { IconCross, IconCheck } from '../icons';
import { useTimeInput } from './useTimeInput';

type SizeType = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface Props {
  ariaLabel?: string;
  modelValue?: string;
  size?: SizeType;
  disabled?: boolean;
  options?: string[];
  placeholder?: string;
  /** Крестик очистки; у обязательного поля он только мешает. */
  clearable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  ariaLabel: undefined,
  modelValue: '',
  size: 'md',
  disabled: false,
  options: () => [],
  placeholder: 'HH:mm',
  clearable: true,
});

const emit = defineEmits<TimeInputEmits>();

const input = ref<HTMLInputElement | null>(null);

const { inputValue, isFocused, lastValidValue, lastEmittedValue, handleInput, handleKeyDown, handleFocus, handleBlur } =
  useTimeInput(
    {
      get modelValue() {
        return props.modelValue;
      },
      get options() {
        return props.options;
      },
      get disabled() {
        return props.disabled;
      },
    },
    emit,
    input,
  );

// Показывать ли выпадающий список
const shouldShowDropdown = computed(() => {
  return isFocused.value && props.options.length > 0 && !props.disabled;
});

/**
 * Выбор опции
 */
const selectOption = (option: string) => {
  inputValue.value = option;

  if (input.value) {
    input.value.value = option;
  }

  lastValidValue.value = option;
  lastEmittedValue.value = option;

  emit('update:modelValue', option);
  emit('change', option);

  input.value?.blur();
};

// Позиционирование выпадающего списка через floating-ui
const {
  reference: container,
  floating: dropdown,
  dropdownStyles: dropdownStyle,
} = useFloatingPosition({
  open: shouldShowDropdown,
  placement: 'bottom-start',
  widthBehaviour: 'min-match',
  maxHeight: 240,
});

void container.value;
void dropdown.value;

/**
 * Функция для очистки поля
 */
const clearInput = () => {
  inputValue.value = '';
  lastValidValue.value = '';
  lastEmittedValue.value = '';
  emit('update:modelValue', '');
  emit('change', '');
  input.value?.focus();
};

/**
 * Показывать ли иконку очистки
 */
const showClearIcon = computed(() => {
  return props.clearable && inputValue.value && inputValue.value.length > 0;
});

/**
 * Computed классы для размеров
 */
const inputClasses = computed(() => {
  const baseClasses = 'time-input';
  const sizeClass = `time-input-size-${props.size}`;
  const disabledClass = props.disabled ? 'time-input-disabled' : '';
  const focusedClass = isFocused.value ? 'time-input-focused' : '';

  return [baseClasses, sizeClass, disabledClass, focusedClass].filter(Boolean).join(' ');
});

const dropdownClasses = computed(() => {
  const baseClasses = 'time-dropdown';
  const sizeClass = `time-dropdown-size-${props.size}`;

  return [baseClasses, sizeClass].join(' ');
});
</script>

<template>
  <div
    ref="container"
    class="relative"
    v-bind="$attrs"
  >
    <div class="relative">
      <input
        ref="input"
        :value="inputValue"
        type="text"
        :aria-label="ariaLabel"
        :class="inputClasses"
        :placeholder="placeholder"
        :disabled="disabled"
        maxlength="5"
        @input="handleInput"
        @keydown="handleKeyDown"
        @focus="handleFocus"
        @blur="handleBlur"
      />

      <div
        v-if="showClearIcon && !props.disabled"
        class="absolute top-1/2 right-3 -translate-y-1/2 transform cursor-pointer transition-colors hover:text-(--danger)"
        @click="clearInput"
      >
        <IconCross
          :width="12"
          :height="12"
          class="text-(--text-secondary)"
        />
      </div>
    </div>
  </div>

  <OverlayTeleport>
    <Transition name="time-dropdown">
      <div
        v-show="shouldShowDropdown"
        ref="dropdown"
        :class="dropdownClasses"
        :style="dropdownStyle"
        data-testid="dropdown"
      >
        <div v-if="props.options.length > 0">
          <div
            v-for="option in props.options"
            :key="String(option)"
            class="flex cursor-pointer gap-x-1.5 select-none hover:bg-(--surface-hover)"
            :class="[
              `time-option-size-${props.size}`,
              inputValue === String(option) ? 'text-(--accent)' : 'text-(--text-primary)',
            ]"
            :data-testid="`option-${String(option)}`"
            @mousedown.prevent
            @click="selectOption(String(option))"
          >
            <div class="grow">{{ String(option) }}</div>
            <div class="self-center text-(--accent)">
              <IconCheck
                v-if="inputValue === String(option)"
                :width="12"
                :height="11"
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </OverlayTeleport>
</template>

<style scoped>
@reference "../../../tailwind.css";

@layer components {
  .time-input {
    @apply w-full rounded-lg transition-all duration-200 ease-in-out;
    @apply text-(--text-primary);
    @apply border border-(--field-border) bg-(--field-bg);
  }

  .time-input:focus {
    @apply ring-1 ring-(--accent) outline-none;
  }

  .time-input::placeholder {
    @apply text-(--text-secondary);
  }

  .time-input-size-xs {
    @apply h-[28px] px-2 text-xs;
  }

  .time-input-size-sm {
    @apply h-8 px-3 text-sm;
  }

  .time-input-size-md {
    @apply h-[42px] px-3 text-base;
  }

  .time-input-size-lg {
    @apply h-12 px-4 text-lg;
  }

  .time-input-size-xl {
    @apply h-14 px-5 text-xl;
  }

  .time-input-disabled {
    @apply cursor-not-allowed opacity-50;
  }

  /* Позиционирование и max-height управляются JS (floating-ui). */
  .time-dropdown {
    @apply z-(--popup-z-index) flex flex-col overflow-y-auto rounded-md border;
    @apply border-(--border) bg-(--surface-card);
  }

  .time-dropdown-size-xs {
    @apply gap-y-1 py-2;
  }

  .time-dropdown-size-sm {
    @apply gap-y-1 py-3;
  }

  .time-dropdown-size-md {
    @apply gap-y-2 py-4.5;
  }

  .time-dropdown-size-lg {
    @apply gap-y-2 py-5;
  }

  .time-dropdown-size-xl {
    @apply gap-y-3 py-6;
  }

  .time-option-size-xs {
    @apply px-3 py-1 text-xs;
  }

  .time-option-size-sm {
    @apply px-4 py-2 text-sm;
  }

  .time-option-size-md {
    @apply px-[30px] py-[12px] text-base;
  }

  .time-option-size-lg {
    @apply px-6 py-3 text-lg;
  }

  .time-option-size-xl {
    @apply px-8 py-4 text-xl;
  }

  .time-dropdown-enter-active,
  .time-dropdown-leave-active {
    transition: all 0.1s ease-in-out;
  }

  .time-dropdown-enter-from,
  .time-dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  .time-dropdown-enter-to,
  .time-dropdown-leave-from {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
